import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ShieldAlert } from "lucide-react";
import KillSwitchConfirmDialog from "@/components/marketing/KillSwitchConfirmDialog";
import { useKillSwitch } from "@/hooks/sms/useKillSwitch";

/**
 * Emergency stop — the one control that must be findable in a panic, so it sits
 * in the Settings header rather than at the bottom of a long scroll.
 *
 * Shares `useKillSwitch` and `KillSwitchConfirmDialog` with `StopMarketingButton`
 * on the Marketing surfaces, so there is only ever one kill-switch
 * implementation. Turning it on asks for confirmation; turning it back off is a
 * single tap.
 */
const EmergencyStopCard = ({ className }: { className?: string }) => {
  const { settings, checked, setKillSwitch } = useKillSwitch();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!settings) return null;

  return (
    <Card className={`border-status-danger/30 ${className ?? ""}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-status-danger/10 text-status-danger">
            <ShieldAlert className="h-[18px] w-[18px]" />
          </div>
          <div className="min-w-0 flex-1">
            <Label id="kill-switch-label" htmlFor="kill-switch" className="text-sm font-medium text-foreground">
              Emergency stop
            </Label>
            <p id="kill-switch-desc" className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Stops every marketing text store-wide. Order confirmation, ready, and review texts keep sending.
            </p>
          </div>
          <Switch
            id="kill-switch"
            checked={checked}
            aria-labelledby="kill-switch-label"
            aria-describedby="kill-switch-desc"
            onCheckedChange={(next) => {
              if (next) setDialogOpen(true);
              else setKillSwitch(false);
            }}
          />
        </div>
      </CardContent>

      <KillSwitchConfirmDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onConfirm={() => setKillSwitch(true)}
      />
    </Card>
  );
};

export default EmergencyStopCard;
